import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableBody,
  TableRow,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { Office365GroupContext } from "../../context/Office365GroupContext";

const WSU_RED = "#A60F2D";

const columns = [
  { id: "id",          label: "ID",           minWidth: 100 },
  { id: "displayName", label: "Display Name", minWidth: 150 },
  { id: "description", label: "Description",  minWidth: 170 },
  { id: "mailNickname",label: "Alias",        minWidth: 100 },
  { id: "visibility",  label: "Visibility",   minWidth: 100 },
];

const visibilityColors = {
  Public:           { bg: "#e8f5e9", border: "#388e3c", color: "#1b5e20" },
  Private:          { bg: "#fff3e0", border: "#f57c00", color: "#e65100" },
  HiddenMembership: { bg: "#fce4ec", border: WSU_RED,   color: WSU_RED   },
};

export const Office365GroupsSearchResultsTable = ({ text }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [state, setState] = useState({ isLoaded: false, error: null });

  const { authState } = useOktaAuth();
  const { groupModule, setGroupModule } = useContext(Office365GroupContext);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
            `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/groups/office365/search`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: authState.accessToken.accessToken,
              },
              body: JSON.stringify({ searchValue: text.trim() }),
            }
        );

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          throw new Error(errorBody?.error?.message || `Error fetching data: ${response.statusText}`);
        }

        const result = await response.json();

        const data = result.Groups.map((group) => ({
          id: group.id,
          displayName: group.displayName,
          description: group.description,
          mailNickname: group.mailNickname,
          visibility: group.visibility,
        }));

        setGroupModule((prev) => ({ ...prev, searchResults: data }));
        setState({ isLoaded: true, error: null });
      } catch (error) {
        setState({ isLoaded: true, error });
      }
    };

    fetchData();
  }, [authState.accessToken.accessToken, text, setGroupModule]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (row) => {
    setGroupModule((prev) => ({
      ...prev,
      mode: "read",
      isOpen: true,
      id: row.id,
      visibility: row.visibility,
      displayName: row.displayName,
    }));
  };

  const renderCell = (column, row) => {
    const value = row[column.id];

    if (column.id === "visibility") {
      const colors = visibilityColors[value] || {};
      return value ? (
          <Chip
              label={value}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                border: `1px solid ${colors.border}`,
                color: colors.color,
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
          />
      ) : (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
      );
    }

    return value ? (
        <Typography variant="body2">{value}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );
  };

  if (!state.isLoaded) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.error) {
    return (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Search failed
            </Typography>
            <Typography variant="body2">{state.error.message || "Unknown error"}</Typography>
          </Box>
        </Paper>
    );
  }

  if (!groupModule.searchResults || groupModule.searchResults.length === 0) {
    return (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 3, py: 3 }}>
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              No groups found for "{text}"
            </Typography>
          </Box>
        </Paper>
    );
  }

  return (
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                    <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          minWidth: column.minWidth,
                          backgroundColor: "#4D4D4D",
                          color: "white",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          letterSpacing: 0.5,
                        }}
                    >
                      {column.label}
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {groupModule.searchResults
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                      <TableRow
                          key={row.id}
                          hover
                          onClick={() => handleRowClick(row)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#fdf0f2" },
                            "&:last-child td": { borderBottom: 0 },
                          }}
                      >
                        {columns.map((column) => (
                            <TableCell key={`${row.id}-${column.id}`} align="center">
                              {renderCell(column, row)}
                            </TableCell>
                        ))}
                      </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={groupModule.searchResults.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #e0e0e0",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.8rem",
              },
            }}
        />
      </Paper>
  );
};